/**
 * RL-988 blood test and screening packages, the home-visit settings and the
 * blood tests patients booked in the app (lab desk). Shapes follow
 * ommri-care-api `lab-packages.examples.ts` (PACKAGE_EXAMPLE,
 * SETTINGS_EXAMPLE, APP_BOOKING_EXAMPLE) and `dto/lab-packages.dto.ts`.
 */
export interface LabPackage {
  id: string;
  /** English name (unique), shown in the app. */
  name: string;
  /** Malay name; null = the app shows the English name. */
  nameMs: string | null;
  summary: string | null;
  summaryMs: string | null;
  /** Lab catalogue test names. */
  tests: string[];
  priceSen: number;
  memberPriceSen: number | null;
  /** The sample must be taken within this many days of booking. */
  validityDays: number;
  homeVisitAllowed: boolean;
  /** Hours without food before the sample; null = no fasting. */
  fastingHours: number | null;
  /** Branches selling it; [] = every branch. */
  branchIds: string[];
  panelCovered: boolean;
  sellInApp: boolean;
  sortOrder: number;
  isActive: boolean;
  version: number;
  updatedAt: string;
}

/** POST /lab-packages ("Add package"). */
export interface LabPackageInput {
  name: string;
  nameMs: string | null;
  summary: string | null;
  summaryMs: string | null;
  tests: string[];
  priceSen: number;
  memberPriceSen: number | null;
  validityDays: number;
  homeVisitAllowed: boolean;
  fastingHours: number | null;
  branchIds: string[];
  panelCovered: boolean;
  sellInApp: boolean;
  sortOrder: number;
}

/** PATCH /lab-packages/{id} ("Edit package"; isActive false = switch off). */
export type LabPackageUpdate = Partial<LabPackageInput> & {
  version: number;
  isActive?: boolean;
};

export const LAB_PACKAGE_LIMITS = {
  nameMin: 2,
  nameMax: 120,
  summaryMax: 200,
  testsMax: 30,
  priceMaxSen: 10_000_000,
  validityMin: 1,
  validityMax: 365,
  fastingMin: 1,
  fastingMax: 24,
  sortMax: 1000,
} as const;

/** GET/PUT /lab-packages/settings (PUT: fields sent replace). */
export interface HomeVisitSettings {
  /** null = home visits are not offered. */
  homeVisitFeeSen: number | null;
  homeVisitBranchIds: string[];
  homeVisitWindowMinutes: number;
  /** Minutes after midnight, branch time. */
  homeVisitFirstStartMin: number;
  homeVisitLastStartMin: number;
  homeVisitsPerWindow: number;
  homeVisitChangeCutoffHours: number;
  /** Read only: the named branches. */
  homeVisitBranches?: { id: string; name: string }[];
}

export type HomeVisitSettingsInput = Omit<
  HomeVisitSettings,
  'homeVisitBranches'
>;

export const HOME_VISIT_WINDOWS = [15, 30, 45, 60, 90, 120] as const;
export const HOME_VISIT_LIMITS = {
  feeMaxSen: 1_000_000,
  perWindowMax: 20,
  cutoffMin: 1,
  cutoffMax: 72,
} as const;

export type LabBookingKind = 'package' | 'doctor_order';
export type LabBookingMode = 'clinic' | 'home';
export type LabBookingStatus = 'booked' | 'cancelled';

/** GET /lab/app-bookings row. */
export interface LabAppBooking {
  id: string;
  kind: LabBookingKind;
  mode: LabBookingMode;
  status: LabBookingStatus;
  /** Sample time (branch visit) or home-visit window start. */
  startsAt: string;
  endsAt: string;
  homeAddress: string | null;
  fastingHours: number | null;
  priceSen: number | null;
  homeFeeSen: number | null;
  patient: { id: string; fullName: string; phone: string | null };
  branch: { id: string; name: string };
  package: { id: string; name: string } | null;
  payer: { id: string; name: string } | null;
  labOrder: {
    id: string;
    number: string | null;
    status: string;
    orderedBy: { id: string; fullName: string } | null;
  };
  appointmentId: string | null;
  billId: string | null;
  cancelledAt: string | null;
  /** Staff only: never shown in the app. */
  cancelReason: string | null;
  createdAt: string;
  version: number;
}

export interface LabAppBookingsQuery {
  /** upcoming = live bookings from today; all = the latest 200 incl. cancelled. */
  scope: 'upcoming' | 'all';
  mode: LabBookingMode | '';
}

export const CANCEL_REASON_LIMITS = { min: 3, max: 300 } as const;
